import { AxiosRequest } from "_services/api";
import { HttpError } from "_services/api/HttpError";
import { HttpResponse } from "_services/api/HttpResponse";
import { IData, IHttpError } from "_services/api/interface/HttpResponse";
import { IOptions } from "components/DropdownSelect";
import { useCallback, useState } from "react";
import { ILancamentoCriarLote, IProcessoEmLote } from "../interface/IProcessoEmLote";
import { toastAtencao, toastErro } from "components/Toast";
import { ordenarBeneficiariosComTaxa } from "../constants/fornecedorTaxa";

export interface IProcessoVincularLoteGP {
  idProcesso: number;
  placa?: string;
  renavam?: string;
  chassi?: string;
  nomeEmpresa?: string;
  nomeDivisao?: string;
  nomeCentroCusto?: string;
  servico?: string;
  aReceber?: number | null;
}

export interface IFiltroProcessoVincularLoteGP {
  processos?: (number | string)[];
  placasChassis?: string[];
  empresa?: number;
  divisao?: number;
  servico?: number;
  centroCusto?: number;
}

export interface IOptionsDescricaoGP {
  value: number;
  label: string;
  valorServico: string | null;
}

export function useCriarLote() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const api = AxiosRequest.makeApi();
  const [loading, setLoading] = useState(false);
  const [optionsBeneficiarios, setOptionsBeneficiarios] = useState<IOptions[]>([]);
  const [processosVincular, setProcessosVincular] = useState<IProcessoVincularLoteGP[]>([]);
  const [selectedRowsVincular, setSelectedRowsVincular] = useState<IProcessoVincularLoteGP[]>([]);
  const [clearRowsVincular, setClearRowsVincular] = useState(false);
  const [optionsDescricao, setOptionsDescricao] = useState<IOptionsDescricaoGP[]>([]);

  const carregarBeneficiarios = useCallback(async () => {
    try {
      const { data } = await msAw.get(`/fornecedores?limite=0&semFiltroStatus=SIM`);
      const options: IOptions[] = data.itens.map((f: { id: number; nomeRazaoSocial: string }) => ({
        value: f.id,
        label: f.nomeRazaoSocial,
      }));
      setOptionsBeneficiarios(ordenarBeneficiariosComTaxa(options));
    } catch {
      toastErro("Erro ao carregar lista de beneficiários");
    }
  }, []);

  const carregarDescricoes = useCallback(async (idFornecedor?: number) => {
    const params = idFornecedor ? `idFornecedor=${idFornecedor}` : "";
    try {
      const { data } = await msAw.get(`/financeiro/descricao/?${params}`);
      setOptionsDescricao(
        data.map((item: { idServico: number; nomeServico: string; valor?: string }) => ({
          value: item.idServico,
          label: item.nomeServico,
          valorServico: item.valor ?? null,
        })),
      );
    } catch {
      setOptionsDescricao([]);
      toastErro("Erro ao carregar lista de custos");
    }
  }, []);

  const buscarServicosAReceber = async (
    pares: { idProcesso: number; idDescricao: number }[],
  ): Promise<{ idProcesso: number; aReceber: number }[]> => {
    if (!pares.length) return [];
    try {
      const params = `pares=${encodeURIComponent(JSON.stringify(pares))}`;
      const { data } = await msAw.get(`/pagamentos/lote/servicos-receber?${params}`);
      return data ?? [];
    } catch {
      return [];
    }
  };

  const buildFiltroParams = (filtros: IFiltroProcessoVincularLoteGP) => {
    const obj: Record<string, any> = {};

    if (filtros.processos?.length) obj.processos = JSON.stringify(filtros.processos);
    if (filtros.placasChassis?.length) obj.placasChassis = JSON.stringify(filtros.placasChassis);
    if (filtros.empresa) obj.empresa = filtros.empresa;
    if (filtros.divisao) obj.divisao = filtros.divisao;
    if (filtros.servico && filtros.empresa) obj.servico = filtros.servico;
    if (filtros.centroCusto) obj.centroCusto = filtros.centroCusto;

    let params = Object.keys(obj)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
      .join("&");

    if (params && !filtros.placasChassis?.length && !filtros.processos?.length) {
      params = `${params}&ultimosXDias=90`;
    }

    return params;
  };

  const buscarProcessosVincular = useCallback(async (filtros: IFiltroProcessoVincularLoteGP) => {
    const params = buildFiltroParams(filtros);
    if (!params) {
      toastAtencao("Selecione pelo menos um filtro");
      return;
    }

    setLoading(true);
    setClearRowsVincular(true);
    try {
      const { status, data } = await msAw.get(`/processo/vincular/lote?${params}`);
      setProcessosVincular(data);
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      setProcessosVincular([]);
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
      setClearRowsVincular(false);
    }
  }, []);

  const validarProcessos = async (processos: (number | string)[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const validarPlacasChassis = async (placasChassis: string[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/placa/chassi/validar/?placasChassis=${JSON.stringify(placasChassis)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const salvarLote = async (params: {
    idBeneficiario: number;
    numeroNfRecibo: string;
    valorNfRecibo: string;
    arquivoRecibo: File | null;
    observacao: string;
    pagamentoUrgente: boolean;
    lancamentos: ILancamentoCriarLote[];
  }) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("idBeneficiario", String(params.idBeneficiario));
      formData.append("tipoLancamento", "C");
      formData.append("numeroNfRecibo", params.numeroNfRecibo);
      formData.append("valorNfRecibo", params.valorNfRecibo);
      formData.append("observacao", params.observacao ?? "");
      formData.append("pagamentoUrgente", String(params.pagamentoUrgente));
      formData.append("lancamentos", JSON.stringify(params.lancamentos));
      if (params.arquivoRecibo) {
        formData.append("arquivoRecibo", params.arquivoRecibo);
      }

      const { status, data } = await msAw.post(`/pagamentos/lote`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const buscarLoteParaEdicao = useCallback(async (idLote: number) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(`/pagamentos/lote/${idLote}/editar`);
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro("Erro ao carregar os dados do lote");
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  }, []);

  const atualizarLote = async (
    idLote: number,
    params: {
      idBeneficiario: number;
      numeroNfRecibo: string;
      valorNfRecibo: string;
      arquivoRecibo: File | null;
      observacao: string;
      pagamentoUrgente: boolean;
      lancamentos: ILancamentoCriarLote[];
    },
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("idBeneficiario", String(params.idBeneficiario));
      formData.append("tipoLancamento", "C");
      formData.append("numeroNfRecibo", params.numeroNfRecibo);
      formData.append("valorNfRecibo", params.valorNfRecibo);
      formData.append("observacao", params.observacao ?? "");
      formData.append("pagamentoUrgente", String(params.pagamentoUrgente));
      formData.append("lancamentos", JSON.stringify(params.lancamentos));
      if (params.arquivoRecibo) {
        formData.append("arquivoRecibo", params.arquivoRecibo);
      }

      const { status, data } = await msAw.put(`/pagamentos/lote/${idLote}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const enviarBoleto = async (idLote: number, arquivo: File) => {
    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);
      const { status, data } = await msAw.post(`/pagamentos/lote/${idLote}/boleto`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const onClickProcesso = async (idProcesso: number) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/processos/param/${idProcesso}`);
      window.open(
        `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${data}`,
        "_blank",
      );
    } catch (error: IHttpError<IData, any> | any) {
      toastErro("Não foi possível abrir o processo");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    setLoading,
    optionsBeneficiarios,
    carregarBeneficiarios,
    processosVincular,
    setProcessosVincular,
    selectedRowsVincular,
    setSelectedRowsVincular,
    clearRowsVincular,
    setClearRowsVincular,
    buscarProcessosVincular,
    validarProcessos,
    validarPlacasChassis,
    optionsDescricao,
    carregarDescricoes,
    buscarServicosAReceber,
    salvarLote,
    buscarLoteParaEdicao,
    atualizarLote,
    enviarBoleto,
    onClickProcesso,
  };
}
